<template>
  <div class="search-bar-container">
    <div class="search-bar">
      <div class="input-wrapper">
        <label
          for="search-input"
          class="search-icon"
        >
          <Icon
            name="lucide:search"
            :aria-label="$t('search.searchSite')"
          />
        </label>
        <form
          class="search-form"
          @submit.prevent="handleSearchSubmit"
        >
          <input
            id="search-input"
            ref="searchInputRef"
            v-model="searchTerm"
            :placeholder="placeholderText"
            :aria-label="$t('search.searchInputAriaLabel')"
            class="search-input"
            type="text"
            spellcheck="false"
            data-testid="search-input-field"
            autocomplete="off"
            @focus="isSearchInputFocused = true"
            @blur="isSearchInputFocused = false"
          />
          <button
            v-if="searchTerm.length > 0"
            type="button"
            class="clear-search-button"
            :aria-label="$t('search.clearSearch')"
            @click="clearSearchInput"
          >
            <Icon
              name="lucide:x"
              size="16"
            />
          </button>
          <button
            type="submit"
            :disabled="searchTerm.length === 0"
            class="search-button"
          >
            {{ $t('search.searchButtonText') }}
          </button>
        </form>
      </div>
    </div>
    <RecentSearches
      :is-search-input-focused="isSearchInputFocused"
      :search-input-ref="searchInputRef"
      :has-searched="hasSearched"
      @selected-recent-search="handleRecentSearchSelection($event)"
    />
    <SearchFilters
      @apply-filters="handleApplyFilters"
      @focusin="handleFiltersSectionFocus"
    />
  </div>
</template>

<script setup>
import { ref, useTemplateRef, onMounted, watch } from 'vue';
import { trackSearchSubmitted } from '~/helpers/searchAnalytics';
import { useRecentSearches } from '@/composables/useRecentSearches';
import RecentSearches from './RecentSearches.vue';
import SearchFilters from './SearchFilters/SearchFilters.vue';

const searchInputRef = useTemplateRef('searchInputRef');

const { $t } = useNuxtApp();
const props = defineProps({
  searchSuggestions: {
    type: Array,
    default: () => [],
  },
  initialSearchTerm: {
    type: String,
    default: '',
  },
});

const searchTerm = ref(props.initialSearchTerm || '');
const placeholderText = ref($t('search.whatCanWeHelpYouFind'));
const emit = defineEmits(['searchTerm', 'fullSearchInitiated']);

const isSearchInputFocused = ref(false);
const hasSearched = ref(false);
const { addToRecentSearches, closeRecentSearchesDropdown } = useRecentSearches(isSearchInputFocused, hasSearched);

watch(searchTerm, (searchTerm) => {
  if (searchTerm?.length > 0) {
    hasSearched.value = true;
  }
});
onMounted(() => {
  searchInputRef.value?.focus();
});

function varyPlaceholderText() {
  const placeholderOptions = [
    $t('search.whatCanWeHelpYouFind'),
    $t('search.startTypingForAnswers'),
    $t('search.lookingForSomething'),
  ];
  const randomIndex = Math.floor(Math.random() * placeholderOptions.length);
  return placeholderOptions[randomIndex];
}

function handleSearchSubmit() {
  trackSearchSubmitted(searchTerm.value, searchTerm.value.length);
  emit('fullSearchInitiated', { q: searchTerm.value });
  addToRecentSearches(searchTerm.value);
  placeholderText.value = varyPlaceholderText();
}

function handleRecentSearchSelection(search) {
  searchTerm.value = search;
  handleSearchSubmit();
}

function clearSearchInput() {
  searchTerm.value = '';
  placeholderText.value = varyPlaceholderText();
}

function handleFiltersSectionFocus() {
  closeRecentSearchesDropdown();
}

function handleApplyFilters(filterUpdates) {
  // Do not trigger a search if there is no term and no meaningful filter values are being applied
  if (
    !searchTerm.value.trim() &&
    !Object.values(filterUpdates).some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)))
  )
    return;
  // Always include current searchTerm when filters change to sync current search term with filters
  emit('fullSearchInitiated', { q: searchTerm.value, ...filterUpdates });
}

// function truncateString(str, maxLength = 50, ellipsis = '...') {
//   if (typeof str !== 'string' || str.length === 0) return '';
//   return str.length > maxLength ? `${str.slice(0, maxLength)}${ellipsis}` : str;
// }

// function link(result) {
//   switch (result.type) {
//     case 'live_feed':
//       return `${result.link}/live-feed#${result.id}`;
//     case 'directory':
//       return `${result.link}/staff?=${result.id}`;
//     case 'cms_page':
//       return `${result.link}/page/${result.slug}`;
//     case 'form':
//       return `${result.link}/forms/${result.id}`;
//     case 'scores_schedule':
//       return `${result.link}/athletics#${result.id}`;
//     case 'event':
//       return `${result.link}/events?=${result.id}`;
//     case 'article':
//       return `${result.link}/article/${result.id}`;
//     case 'public_file':
//     case 'documents_asset':
//       return `${result.url}`;
//     case 'cms_link':
//       return `${result.content.url}`;
//     default:
//       return `${result.link}`;
//   }
// }

// function resultTitle(result) {
//   switch (result.type) {
//     case 'directory':
//       return result.full_name;
//     case 'scores_schedule':
//       return result.title || `${result.home_team} vs ${result.away_team}`;
//     case 'event':
//     case 'article':
//       return result.title;
//     case 'live_feed':
//       return truncateString(result.status);
//     case 'form':
//     case 'public_file':
//     case 'documents_asset':
//     case 'cms_link':
//     case 'cms_page':
//       return result.name;
//     default:
//       return $t('search.fileName');
//   }
// }

// function resultType(result) {
//   switch (result.type) {
//     case 'directory':
//       return $t('search.staffTitle');
//     case 'cms_page':
//       return $t('search.pageTitle');
//     case 'live_feed':
//       return $t('search.liveFeedTitle');
//     case 'form':
//       return $t('search.formTitle');
//     case 'scores_schedule':
//     case 'event':
//       return $t('search.eventTitle');
//     case 'article':
//       return $t('search.articleTitle');
//     case 'cms_link':
//       return $t('search.linkTitle');
//     default:
//       return $t('search.documentTitle');
//   }
// }
</script>

<style lang="scss" scoped>
@use '../global/scss/mixins/_breakpoints.scss' as bp;

.search-bar-container {
  --color-grey-10: #d3d3d3;
  --color-grey-50: #f7f7f7;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-blue-300: #6e91d7;
  --color-blue-500: #1850c1;
  --border-radius-xs: 4px;
  --color-text-disabled: #616161;
  //recent-searches-section should be higher than advanced-search-filters-section
  --z-index-search-bar: 2;

  z-index: var(--z-index-search-bar);
  position: relative;
  background: var(--primary-white);
  border-radius: 8px;
  overflow: visible;
}

.search-bar {
  box-sizing: border-box;
  padding: 16px;
  padding-bottom: 0;

  .search-form {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
  }

  .input-wrapper {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    padding: 8px 12px;
    gap: 16px;
    border: 1px solid var(--color-grey-200);
    border-radius: var(--border-radius-xs);
    background: var(--color-grey-50);
    width: 100%;
    font-size: 16px;
    &:hover {
      border: 1px var(--color-blue-300) solid;
    }
    &:focus-within {
      border: 1px var(--color-blue-500) solid;
    }

    .search-input {
      display: block;
      border: none;
      background: transparent;
      color: var(--color-text-disabled);
      font-size: 14px;
      font-weight: 450;
      line-height: 16px;
      letter-spacing: 0.4px;
      width: 100%;

      &:focus {
        outline: none;
      }
    }

    .search-icon {
      display: flex;
    }

    .clear-search-button {
      display: flex;
      border: none;
      background: none;
      padding: 0;

      &:hover {
        cursor: pointer;
      }
    }

    .search-button {
      padding: 8px 16px;
      background-color: var(--color-grey-100);
      color: var(--color-text-disabled);
      border-radius: 1000px;
      border-style: none;
      &:focus-visible {
        outline: 2px solid var(--color-blue-500);
        outline-offset: 1px;
      }
      &:hover {
        cursor: pointer;
      }
    }

    /* When input has value, change button color to primary */
    .search-input:not(:placeholder-shown) ~ .search-button {
      background: var(--primary-button-background);
      color: var(--primary-button-text-contrast);
      border-radius: var(--primary-button-border-radius);
      border: var(--primary-button-border);
      &:hover {
        background-color: var(--primary-button-hover);
      }
    }
  }
}

.suggestions-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  background: var(--primary-white);
  border-radius: 8px;
  border: 1px solid var(--color-grey-10);
  box-shadow: 0px -6px 20px 0px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  padding: 16px 0px;
  margin-top: -8px;
  margin-left: 16px;
  margin-right: 16px;

  .result {
    display: flex;
    align-items: center;
    padding: 12px 0 12px 16px;
    font-size: 14px;
    font-weight: 450;
    line-height: 20px;
    letter-spacing: 0.6px;

    &:hover {
      background: color-mix(in srgb, var(--primary-button-background) 10%, #fff);
    }

    .search-link {
      display: flex;
      align-items: center;
      text-decoration: none;
      color: #7f7f7f;
      gap: 8px;
      width: 100%;

      .file-type {
        background-repeat: no-repeat;
        background-size: cover;
        height: 20px;
        width: 20px;
      }
      .result-arrow .iconify {
        display: flex;
      }
    }
  }
}

@include bp.screen('xs') {
  .search-bar-container {
    border-radius: 0;
  }
}
</style>
